import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Paper, Tabs, Divider, Alert } from "@mantine/core";
import { FaExclamationTriangle } from "react-icons/fa";
import {
  MobileMoneyAccountList,
  BankAccountGrid,
  // WalletAccountGrid,
  PaymentHeader,
  PaymentSection,
} from "./components";
import {
  AddMobileMoneyModal,
  AddBankAccountModal,
  // AddPaykitWalletAccountModal,
} from "./components/modals";
import {
  useMobileMoneyHandlers,
  useBankAccountHandlers,
  // useWalletAccountHandlers,
} from "./components/hooks";
import {
  PAYMENT_TABS,
  BUTTON_CONFIG,
  ALERT_MESSAGES,
} from "./components/config/accountConfig";
import CustomTabs from "../ui/CustomTabs/CustomTabs";

const VALID_TABS = new Set(["mpesa", "bank" /*, "paykit" */]);

const PaymentPageContent = ({
  mobileMoneyAccounts,
  bankAccounts,
  // walletAccounts,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.has(searchParams.get("paymentTab"))
    ? searchParams.get("paymentTab")
    : "mpesa";
  const setActiveTab = (tab) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("paymentTab", tab);
      return next;
    }, { replace: true });
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    if (!searchParams.get("paymentTab")) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("paymentTab", "mpesa");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const mobileMoneyHandlers = useMobileMoneyHandlers();
  const bankAccountHandlers = useBankAccountHandlers();
  // const walletAccountHandlers = useWalletAccountHandlers();

  const tabOptions = useMemo(
    () =>
      PAYMENT_TABS.map((tab) => ({
        ...tab,
        icon: <tab.icon size={16} />,
      })),
    [],
  );

  const handlerActions = useMemo(
    () => ({
      mpesa: mobileMoneyHandlers.openModal,
      bank: bankAccountHandlers.openModal,
      // paykit: walletAccountHandlers.openModal,
    }),
    [
      mobileMoneyHandlers.openModal,
      bankAccountHandlers.openModal,
      // walletAccountHandlers.openModal,
    ],
  );

  const buttonConfig = useMemo(() => {
    const config = BUTTON_CONFIG[activeTab];
    if (!config) {
      return {
        text: "Add Account",
        icon: null,
        action: () => {},
      };
    }

    const IconComponent = config.icon;
    return {
      text: config.text,
      icon: <IconComponent size={16} />,
      action: handlerActions[activeTab] || (() => {}),
    };
  }, [activeTab, handlerActions]);

  return (
    <Paper shadow="xs" radius="md" p={{ base: "sm", sm: "xl" }} className="bg-white">
      <PaymentHeader
        title="Payment Information"
        description="Company payment details overview"
        buttonText={buttonConfig.text}
        buttonIcon={buttonConfig.icon}
        onButtonClick={buttonConfig.action}
      />

      {showAlert && (
        <Alert
          icon={<FaExclamationTriangle size={14} />}
          title={ALERT_MESSAGES.UPDATE_CONTACT_SUPPORT}
          color="yellow"
          radius="lg"
          variant="light"
          withCloseButton
          onClose={() => setShowAlert(false)}
          className="mb-6"
        />
      )}

      <CustomTabs
        tabs={tabOptions}
        value={activeTab}
        onChange={setActiveTab}
        grow={true}
      />

      <Tabs value={activeTab} onChange={setActiveTab} className="mt-4">
        <Tabs.Panel value="mpesa">
          <PaymentSection>
            <MobileMoneyAccountList accounts={mobileMoneyAccounts} />
          </PaymentSection>
        </Tabs.Panel>
        <Tabs.Panel value="bank">
          <PaymentSection>
            <BankAccountGrid accounts={bankAccounts} />
          </PaymentSection>
        </Tabs.Panel>
        {/* <Tabs.Panel value="paykit">
          <PaymentSection>
            <WalletAccountGrid accounts={walletAccounts} />
          </PaymentSection>
        </Tabs.Panel> */}
      </Tabs>

      <Divider my="lg" />

      <AddMobileMoneyModal
        opened={mobileMoneyHandlers.modalOpened}
        onClose={mobileMoneyHandlers.closeModal}
        onSubmit={mobileMoneyHandlers.handleSubmit}
        control={mobileMoneyHandlers.control}
        isSubmitting={mobileMoneyHandlers.isSubmitting}
      />

      <AddBankAccountModal
        opened={bankAccountHandlers.modalOpened}
        onClose={bankAccountHandlers.closeModal}
        currentStep={bankAccountHandlers.currentStep}
        step1Draft={bankAccountHandlers.step1Draft}
        verifiedData={bankAccountHandlers.verifiedData}
        isVerifying={bankAccountHandlers.isVerifying}
        isSaving={bankAccountHandlers.isSaving}
        goToStep2={bankAccountHandlers.goToStep2}
        goBackToStep1={bankAccountHandlers.goBackToStep1}
        handleSave={bankAccountHandlers.handleSave}
        setStep1Draft={bankAccountHandlers.setStep1Draft}
        verifyBankAccount={bankAccountHandlers.verifyBankAccount}
        verifyViaMsisdn={bankAccountHandlers.verifyViaMsisdn}
      />

      {/* <AddPaykitWalletAccountModal
        opened={walletAccountHandlers.modalOpened}
        onClose={walletAccountHandlers.closeModal}
        onSubmit={walletAccountHandlers.handleSubmit}
        control={walletAccountHandlers.control}
        isSubmitting={walletAccountHandlers.isSubmitting}
      /> */}
    </Paper>
  );
};

export default PaymentPageContent;
